import { claveIdempotencia, type Operaciones, type RespuestaDosPasosEnElMomento } from './operaciones';
import { ErrorApi } from './problema';

/**
 * Dos pasos «en el momento» (G116, ADR-0045; texto de la ventana aprobado en G123). Una operación con
 * `x-two-factor: session` responde `401 two_factor_confirmation_required` si la cuenta tiene los dos pasos y la sesión no
 * los confirmó en los últimos 10 minutos. La puerta lo comprueba **antes** de la idempotencia, así que la clave no se
 * guarda contra ese 401: el panel pide el código (`pedirCodigo`, la ventana RV Ventana con el campo de 6 cifras), lo
 * confirma con `confirmSessionTwoFactor` y **repite la misma petición con la misma `Idempotency-Key`**. Si la persona
 * cancela la ventana, la operación termina con el mismo error.
 *
 * Uso:
 *
 *     const pedirCodigo = usePedirCodigoDosPasos();
 *     await conDosPasosEnElMomento(ops, pedirCodigo, (clave) => ops.unaOperacion(negocio, datos, clave));
 */
/** Pide el código en la ventana; `fallido` = el anterior no valía (`code_invalid`). null = la persona canceló. */
export type PedirCodigo = (fallido: boolean) => Promise<RespuestaDosPasosEnElMomento | null>;

export function pideDosPasosEnElMomento(error: unknown): boolean {
    return error instanceof ErrorApi && error.codigo === 'two_factor_confirmation_required';
}

export async function conDosPasosEnElMomento<T>(
    ops: Pick<Operaciones, 'confirmSessionTwoFactor'>,
    pedirCodigo: PedirCodigo,
    operacion: (clave: string) => Promise<T>,
    clave: string = claveIdempotencia(),
): Promise<T> {
    try {
        return await operacion(clave);
    } catch (error) {
        if (!pideDosPasosEnElMomento(error)) {
            throw error;
        }
        let fallido = false;
        // La ventana se queda abierta mientras el código no valga: cada intento fallido vuelve a pedirlo con su error.
        for (;;) {
            const respuesta = await pedirCodigo(fallido);
            if (respuesta === null) {
                throw error;
            }
            try {
                await ops.confirmSessionTwoFactor(respuesta);
                break;
            } catch (fallo) {
                if (fallo instanceof ErrorApi && fallo.codigo === 'code_invalid') {
                    fallido = true;
                    continue;
                }
                throw fallo;
            }
        }
        // Misma petición, misma clave: si la primera llegó a hacerse, la API devuelve la respuesta guardada.
        return operacion(clave);
    }
}
